import { AlertStatusEnum } from "~/feature/event/components/AlertSection/types";
import type { Schemas } from "~/types/generated";

export enum EventSortByEnum {
  CREATED_AT = "createdAt",
  UPDATED_AT = "updatedAt",
  TITLE = "title",
  TICKER = "ticker",
  VOLUME = "volume",
  VOLUME24 = "volume24",
  LIQUIDITY = "liquidity",
  START_DATE = "startDate",
  END_DATE = "endDate",
  CATEGORY = "category",
  PLATFORM = "platform",
  PRICE = "price",
  CONSENSUS = "consensus",
  VOLUME24PERS = "volume24pers",
  VOLUME7PERS = "volume7pers",
  SPREAD = "spread",
  SEARCH_RELEVANCE = "searchRelevance",
}

export enum MarketSortByEnum {
  VOLUME = "volume",
  CHANCE = "chance",
  PRICE = "price",
  LIQUIDITY = "liquidity",
  TITLE = "title",
  VOLUME24PERC = "volume24pers",
  VOLUME7PERC = "volume7pers",
  END_DATE = "endDate",
  OPEN_INTEREST = "openInterest",
  RESULT = "result",
  BEST_BUY = "bestBuy",
  BEST_SELL = "bestSell",
  SPREAD = "spread",
  PLATFORM_COUNT = "platformCount",
}

/**
 * How the API ranks a group of matched markets against the other rows (PH-1116). The event detail
 * grid ranks a group by the consensus % chance its own group row displays (`consensus`); the
 * dashboard's expanded panel ranks whole outcomes by their PH Consensus, highest first, outcomes
 * without one last (`outcome`, PH-1359). `main` — the API default — ranks a group by its best
 * single market.
 */
export enum MarketGroupRankEnum {
  MAIN = "main",
  CONSENSUS = "consensus",
  OUTCOME = "outcome",
}

export enum MarketStatus {
  ACTIVE = "active",
  RESOLVED = "resolved",
  CLOSED = "closed",
  PROPOSED = "proposed",
  DISPUTED = "disputed",
}

/**
 * Market status as it arrives on the wire — the string-literal union the
 * OpenAPI contract emits for `MarketResponseDto.unifiedStatus`. Identical
 * values to `MarketStatus`, but assignable from generated DTO fields (plain
 * string unions, not the nominal enum). Use to type props/params that consume
 * `unifiedStatus` off a contract type.
 */
export type MarketStatusValue = `${MarketStatus}`;

export enum SettlementResult {
  YES = "yes",
  NO = "no",
}

export enum MarketQualityLevel {
  WITHHELD = "withheld",
  CAUTION = "caution",
  OK = "ok",
}

export type MarketQualityLevelValue = `${MarketQualityLevel}`;

export enum WithheldReason {
  UNTRADED = "untraded",
  IMPOSSIBLE_PRICE = "impossible-price",
  CROSSED = "crossed",
  NO_ORDERS = "no-orders",
  WIDE_NO_TRADE_PRICE = "wide-no-trade-price",
  OUT_OF_LINE = "out-of-line",
}

export type WithheldReasonValue = `${WithheldReason}`;

export enum CautionReason {
  NO_BIDS = "no-bids",
  NO_ASKS = "no-asks",
  VERY_WIDE_SPREAD = "very-wide-spread",
}

export type CautionReasonValue = `${CautionReason}`;

/**
 * A market is the backend contract — generated from OpenAPI, not hand-written.
 * See ../../docs/CONTRACT_TESTING.md.
 */
export type IMarket = Schemas["MarketResponseDto"];

export type IOutcomeConsensus = Schemas["OutcomeConsensusDto"];

export type IOutcomeExcludedMarket = Schemas["OutcomeExcludedMarketDto"];

export enum ExcludedReason {
  WITHHELD = "withheld",
  UNTRADED = "untraded",
  SETTLED = "settled",
  DISPUTED = "disputed",
}

export type ExcludedReasonValue = `${ExcludedReason}`;

/**
 * A single platform's event within an aggregate — the backend contract. See
 * ../../docs/CONTRACT_TESTING.md.
 */
export type IGroupedEvent = Schemas["EventResponseDto"];

export interface IResolutionRules {
  consistencyConfirmation?: string;
  coreResolutionLogic?: string[];
  edgeCases?: { name: string; value: string }[];
  primaryResolutionSource?: string;
  timing?: string;
  status: AlertStatusEnum;
  issue?: string;
  heroTip?: string;
  divergences?: { platform: string; summary: string }[];
}

/**
 * Per-platform official rules entry — the backend contract. See
 * ../../docs/CONTRACT_TESTING.md.
 */
export type IOfficialPlatformsRules = Schemas["OfficialRulesEntryDto"];

/**
 * A fully aggregated event (detail + list surfaces) is the backend contract —
 * generated from OpenAPI, not hand-written. See ../../docs/CONTRACT_TESTING.md.
 *
 * Two fields are overridden with the FE's structured shapes because the
 * contract declares them as opaque JSON (`{ [key: string]: unknown }`), which
 * the FE cannot consume directly:
 *   - `resolutionRulesDetails` → `IResolutionRules` (rendered field-by-field in
 *     EventContent).
 *   - `resolutionData` → `{ resolvedAt?; winningMarketIds? }` (winningMarketIds
 *     drives the resolved-market highlight; the list DTO already models this as
 *     ResolutionDataListDto).
 * Backend follow-up: give both a proper nested DTO so these overrides can go.
 * `IRelatedEvent` is spliced in for `relatedEvents` — the contract's
 * RelatedEventItemDto (via IRelatedEvent) matches, so it flows through.
 */
export type IAggregatedEvent = Omit<
  Schemas["AggregatedEventResponseDto"],
  "resolutionRulesDetails" | "resolutionData"
> & {
  resolutionRulesDetails?: IResolutionRules;
  resolutionData?: {
    resolvedAt?: string;
    winningMarketIds?: string[];
  };
};
